import { SidebarVariant } from "@/components/layout/components/sidebar/constants";
import { clientEnvModule } from "@/modules/client-env";
import { isMac } from "@/domains/platform/isMac";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { BackForwardButtons } from "../back-forward-buttons";

interface SidebarDesktopSectionProps {
  currentSidebarVariant: SidebarVariant;
  toggleSidebar: () => void;
  navigateBack: () => void;
  navigateForward: () => void;
  canNavigateBack: boolean;
  canNavigateForward: boolean;
}

export const SidebarDesktopSection = observer(function SidebarDesktopSection({
  currentSidebarVariant,
  navigateBack,
  navigateForward,
  canNavigateBack,
  canNavigateForward,
}: SidebarDesktopSectionProps) {
  const isDesktop = clientEnvModule.isDesktop();
  const isMacOS = isMac();

  const desktopChromeSpacer = isMacOS ? (
    <StoplightSpacer data-component="StoplightSpacer" />
  ) : (
    <DesktopMenuOverlaySpacer data-component="OverlaySpacer" />
  );

  if (!isDesktop) return null;

  if (currentSidebarVariant === SidebarVariant.Collapsed) {
    return (
      <StyledWrapper $sidebarVariant={currentSidebarVariant} data-component="SidebarDesktopSection">
        {desktopChromeSpacer}
      </StyledWrapper>
    );
  }

  return (
    <StyledWrapper $sidebarVariant={currentSidebarVariant} data-component="SidebarDesktopSection">
      <LeftSection>{desktopChromeSpacer}</LeftSection>
      <RightSection>
        <BackForwardButtons
          navigateBack={navigateBack}
          navigateForward={navigateForward}
          canNavigateBack={canNavigateBack}
          canNavigateForward={canNavigateForward}
        />
      </RightSection>
    </StyledWrapper>
  );
});

const StyledWrapper = styled.div<{ $sidebarVariant: SidebarVariant }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  justify-content: ${({ $sidebarVariant }) =>
    $sidebarVariant === SidebarVariant.Collapsed ? "center" : "space-between"};
  align-items: center;
  min-height: 40px;
  padding-inline: ${({ theme }) => theme.spacing["4"]};
  padding-top: ${({ theme }) => theme.spacing["2"]};
  -webkit-app-region: drag;
`;

const LeftSection = styled.div({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  alignItems: "center",
  width: "100%",
});

const RightSection = styled.div({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
});

export const StoplightSpacer = styled.div({
  height: 20,
  width: 80,
  minWidth: 80,
});

// Space reserved for the global desktop menu overlay (Windows/Linux).
// Matches its 24px left offset plus the approximately 32px button width.
export const DesktopMenuOverlaySpacer = styled.div({
  height: 20,
  width: 56,
});
